import { Dispatch, ReactNode, SetStateAction } from "react";
import { AddressBook } from "../../../Models/AddressBook";
import { ModalConfig } from "../../../Models/ModalConfig";
import { ErrorResponse } from "../../../Models/Error";
import { SearchFilter } from "../../../Models/SearchFilter";

/* ===========================================================================
 * SHARED TYPES
 * =========================================================================== */

/** Available backend services for AddressBook operations */
type ServiceType = "registry" | "logistic" | "shipment";

/* ===========================================================================
 * HANDLER CONFIGURATION
 * =========================================================================== */

/**
 * @interface HandlerConfig
 * Specifies configuration options for enabling or disabling
 * handler-related functionalities.
 * * Responsibilities:
 * - Managing prefill data via `defaultValue`.
 * - Controlling access to registry-specific save and search features.
 */
interface HandlerConfig {
  /** Optional default values used to prefill the AddressBook form. */
  defaultValue?: Partial<AddressBook> | null;

  /** Configuration options concerning registry-related features. */
  registry: {
    /** Determines whether saving to the registry service is enabled. */
    saveEnabled: boolean;
    /** Determines whether registry-based searching is enabled. */
    searchEnabled: boolean;
  };
}

/**
 * @const DEFAULT_HANDLER_CONFIG
 * Standardized default configuration for handlers.
 */
export const DEFAULT_HANDLER_CONFIG: HandlerConfig = {
  registry: {
    saveEnabled: false,
    searchEnabled: false,
  },
  defaultValue: null,
};

/* ===========================================================================
 * INDEX PROVIDER TYPES
 * =========================================================================== */

/**
 * @interface AddressBookIndexProviderProps
 * Properties for the AddressBookIndexProvider component.
 * * Responsibilities:
 * - Providing global-level configuration (service, ownerId).
 * - Passing down lifecycle callbacks and billing flags.
 */
export interface AddressBookIndexProviderProps {
  /** Child components to be rendered within the Provider. */
  children: ReactNode;
  /** Specifies backend service. Defaults to "registry". */
  service?: ServiceType;
  /** Identifies the owner associated with the AddressBook entries. */
  ownerId?: string;
  /** Indicates if the address refers to the authenticated user. */
  myAddress?: boolean;
  /** Flag for billing-specific logic. */
  isBilling?: boolean;
  /** Callback executed after creation, update, or deletion. */
  callback?: () => void;
  /** Optional configuration for enabled/disabled features. */
  handlerConfig?: HandlerConfig;
}

/**
 * @interface AddressBookIndexContextValue
 * Aggregated value exposed by the AddressBookIndex context.
 */
export interface AddressBookIndexContextValue extends HandlerConfig {
  /** Modal configuration object for index-level UI dialogs. */
  modalConfig: ModalConfig;
  /** Setter for updating the modal configuration. */
  setModalConfig: Dispatch<SetStateAction<ModalConfig>>;
  /** ID of the owner associated with the AddressBook. */
  ownerId?: string;
  /** Indicates whether the address belongs to the authenticated user. */
  myAddress: boolean;
  /** Active service backend. */
  service: ServiceType;
  /** Flag for billing-specific logic. */
  isBilling: boolean;
  /** Optional callback invoked after successful operations. */
  callback?: () => void;
}

/* ===========================================================================
 * HANDLER PROVIDER TYPES
 * =========================================================================== */

/**
 * @interface AddressBookHandlerProviderProps
 * Properties for the AddressBookHandlerProvider.
 * * Responsibilities:
 * - Configuring entity persistence (autosave, registry save).
 * - Defining form behavior (action mode, fields to show).
 * - Managing external error reporting.
 */
export interface AddressBookHandlerProviderProps {
  /** React children wrapped by the provider. */
  children: ReactNode;
  /** Optional ID to load an existing entity. Omit for "create" mode. */
  id?: string;
  /** Identifier of the owner. */
  ownerId?: string;
  /** Callback triggered after successful save. */
  callback?: (addressBook?: AddressBook) => void;
  /** Backend service. Defaults to "registry". */
  service?: ServiceType;
  /** Current action (e.g., "create", "edit", "details"). */
  action: string;
  /** Initial loading state. */
  preLoader?: boolean;
  /** Initial form values. */
  defaultValue?: Partial<AddressBook> | null;
  /** Enables real-time updates without explicit save. */
  autosave?: boolean;
  /** Flag for billing-specific logic. */
  isBilling?: boolean;
  /** Configuration for registry features. */
  registry?: {
    saveEnabled: boolean;
    searchEnabled: boolean;
  };
  /** UI Contextual information. */
  label?: string;
  /** Forces validation and error display immediately. */
  inError?: boolean;
  /** Filters to refine AddressBook queries. */
  searchInAddressBookParams?: SearchFilter[];
  /** Visibility map for specific form fields. */
  fieldsToShow?: Record<string, boolean>;
  /** Callback for latest validation or API errors. */
  getErrors?: (errors: ErrorResponse<AddressBook> | null) => void;
}

/**
 * @interface AddressBookHandlerContextValue
 * Operational state and functions for AddressBook management.
 */
export interface AddressBookHandlerContextValue {
  /** ID of the entity (undefined in "create" mode). */
  id?: string;
  /** Current (partial) AddressBook data. */
  data: Partial<AddressBook>;
  /** Backend or validation error details. */
  error: ErrorResponse<AddressBook> | null;
  /** Async operation status. */
  loader: boolean;
  /** Success callback. */
  callback?: (addressBook?: AddressBook) => void;
  /** Modal UI state. */
  modalConfig: ModalConfig;
  /** Modal UI setter. */
  setModalConfig: Dispatch<SetStateAction<ModalConfig>>;
  /** Flag for billing logic. */
  isBilling: boolean;
  /** Autosave status. */
  autosave: boolean;
  /** Resolved owner identifier. */
  ownerId: string;
  /** Registry feature configuration. */
  registry: AddressBookHandlerProviderProps["registry"];
  /** Contextual UI label. */
  label?: string;
  /** Active action mode. */
  action: string;
  /** Active API service. */
  service: ServiceType;
  /** Internal filtering parameters. */
  searchInAddressBookParams?: SearchFilter[];
  /** Field visibility configuration. */
  fieldsToShow: Record<string, boolean>;
  /** Persists entity to registry service. */
  saveRegistryAddressBook: () => void;
  /** Fetches entity by ID. */
  getAddressBook: (id: string) => void;
  /**
   * Validates and persists entity.
   * @param {AddressBook} addressBook - Entity to save.
   * @param {keyof AddressBook | null} property - Field to clear errors for.
   * @param {boolean} save - Whether to trigger API call.
   */
  saveAddressBook: (
    addressBook: AddressBook,
    property: keyof AddressBook | null,
    save: boolean,
  ) => void;
}

/* ===========================================================================
 * DELETE PROVIDER TYPES
 * =========================================================================== */

/**
 * @interface AddressBookDeleteProviderProps
 * Properties for batch deletion management.
 */
export interface AddressBookDeleteProviderProps {
  /** React children. */
  children: ReactNode;
  /** IDs to be deleted. */
  data?: string[];
  /** Post-deletion callback. */
  callback?: () => void;
  /** API service for deletion. Defaults to "registry". */
  service?: ServiceType;
}

/**
 * @interface AddressBookDeleteContextValue
 * Exposed logic for bulk deletion.
 */
export interface AddressBookDeleteContextValue {
  /** Async status of deletion. */
  loader: boolean;
  /** Triggers batch deletion for IDs in `data`. */
  deleteAddressBooks: () => void;
  /** Current IDs pending deletion. */
  data: string[];
  /** Post-deletion callback. */
  callback?: () => void;
  /** Active API service. */
  service: ServiceType;
}
